import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
  ExecutionLog,
  ExecutionLogsService,
} from '../../service/ExecutionLogsService';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import KitModal from '../UI/KitModal';

interface ExecutionLogsModalProps {
  show: boolean;
  onHide: () => void;
  flowId: string;
  nodeId: string;
}

const ExecutionLogsModal = ({
  show,
  onHide,
  flowId,
  nodeId,
}: ExecutionLogsModalProps) => {
  const { user } = useAuthStore(state => state);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const { setNotification } = useNotificationStore();

  useEffect(() => {
    if (show && flowId && nodeId) {
      fetchLogs();
    }
  }, [show, flowId, nodeId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await ExecutionLogsService.getExecutionLogs(
        flowId,
        nodeId,
      );

      setLogs(response);
    } catch (error) {
      setNotification({
        message: 'Error fetching execution logs',
        type: 'error',
      });
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KitModal
      show={show}
      onHide={onHide}
      title="Execution Logs"
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
        },
      }}
    >
      <Box>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : logs.length === 0 ? (
          <Typography
            color={user?.theme === 'dark' ? '#D0D5DD' : '#667085'}
            sx={{ textAlign: 'center', padding: '16px' }}
          >
            No execution logs found
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: user?.theme === 'dark' ? '#101828' : '#FFF',
              maxHeight: '600px',
              overflow: 'auto',
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  >
                    Execution Time
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  >
                    Session ID
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  >
                    Message
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  >
                    Error Stack
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      }}
                    >
                      {dayjs(log.createdAt).format('MMM D, YYYY h:mm A')}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      }}
                    >
                      {log.userName || 'N/A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                      }}
                    >
                      {log.sessionId}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        maxWidth: '300px',
                        wordBreak: 'break-word',
                      }}
                    >
                      {log.message}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.isError ? 'Error' : 'Success'}
                        color={log.isError ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        maxWidth: '200px',
                        padding: '8px',
                      }}
                    >
                      {log.errorStack && (
                        <Box
                          sx={{
                            maxHeight: '50px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            padding: '4px 8px',
                            backgroundColor:
                              user?.theme === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)',
                            borderRadius: '4px',
                            border: `1px solid ${
                              user?.theme === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'
                            }`,
                            cursor: 'pointer',
                            '&::-webkit-scrollbar': {
                              width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor:
                                user?.theme === 'dark'
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(0, 0, 0, 0.05)',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor:
                                user?.theme === 'dark'
                                  ? 'rgba(255, 255, 255, 0.2)'
                                  : 'rgba(0, 0, 0, 0.2)',
                              borderRadius: '3px',
                              '&:hover': {
                                backgroundColor:
                                  user?.theme === 'dark'
                                    ? 'rgba(255, 255, 255, 0.3)'
                                    : 'rgba(0, 0, 0, 0.3)',
                              },
                            },
                          }}
                        >
                          {log.errorStack}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </KitModal>
  );
};

export default ExecutionLogsModal;
